import { useEffect, useRef, useState } from 'react';
import { getSurveyById } from '../../utils/surveyApi';
import { dynamicSectionConfigFromRow, effectiveRubricStatus, type AiDynamicSectionConfig } from './aiDynamicSection';

/** How often to re-check a pending rubric. Save no longer blocks on derivation, so there is no
 *  urgency to the first check — the interval before the FIRST attempt is the same as every other. */
export const RUBRIC_POLL_INTERVAL_MS = 3_000;

/** Hard cap on attempts (≈2 minutes at the interval above) — "do not poll forever": past this, the
 *  checklist area reports it could not confirm the write rather than spinning indefinitely. */
export const RUBRIC_POLL_MAX_ATTEMPTS = 40;

export interface UseRubricStatusPollingParams {
  surveyId: number | null;
  questionId: string;
  /** Whether polling should be running right now — the caller decides this (typically
   *  `effectiveRubricStatus(question.rubric_status) === 'pending'`), so this hook owns only the
   *  timer, not the status logic. */
  isPending: boolean;
  /** Called once the polled row's rubric is no longer pending (ready OR failed) with the fields
   *  the checklist area needs to update itself. Never called after the poll times out. */
  onResolved: (update: Pick<AiDynamicSectionConfig, 'rubric' | 'rubric_status' | 'rubric_stale'>) => void;
}

export interface UseRubricStatusPollingResult {
  /** True once the attempt cap was hit while still pending — the caller should display this the
   *  same as a backend-reported failure. */
  timedOut: boolean;
}

/**
 * Polls `GET /api/surveys/:id` on an interval while `isPending` is true, watching one question's
 * row for its rubric to stop being `pending`. Stops itself as soon as the row resolves, on
 * unmount, or once `RUBRIC_POLL_MAX_ATTEMPTS` is reached.
 */
export function useRubricStatusPolling({
  surveyId,
  questionId,
  isPending,
  onResolved,
}: UseRubricStatusPollingParams): UseRubricStatusPollingResult {
  const [timedOut, setTimedOut] = useState(false);
  const onResolvedRef = useRef(onResolved);
  useEffect(() => {
    onResolvedRef.current = onResolved;
  }, [onResolved]);

  useEffect(() => {
    if (!isPending || surveyId === null) return;

    let cancelled = false;
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;

    const tick = async () => {
      attempts += 1;
      try {
        const { survey } = await getSurveyById(surveyId);
        const row = survey.questions?.find((q) => q.id === questionId);
        if (cancelled) return;

        if (row) {
          const config = dynamicSectionConfigFromRow(row);
          if (effectiveRubricStatus(config.rubric_status) !== 'pending') {
            onResolvedRef.current({
              rubric: config.rubric,
              rubric_status: config.rubric_status,
              rubric_stale: config.rubric_stale,
            });
            return;
          }
        }
      } catch {
        // Transient fetch failure — fall through and retry on the next tick, counted against the
        // same attempt cap so a persistently broken connection still gives up eventually.
      }

      if (cancelled) return;
      if (attempts >= RUBRIC_POLL_MAX_ATTEMPTS) {
        setTimedOut(true);
        return;
      }
      timerId = setTimeout(tick, RUBRIC_POLL_INTERVAL_MS);
    };

    setTimedOut(false);
    timerId = setTimeout(tick, RUBRIC_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timerId !== null) clearTimeout(timerId);
    };
  }, [isPending, surveyId, questionId]);

  // Masked by `isPending` rather than reset with a synchronous setState when it goes false: once
  // polling is not running there is nothing to have timed out, regardless of what the last cycle
  // left behind, and the next pending cycle clears the underlying state itself before its first
  // tick (above).
  return { timedOut: isPending && timedOut };
}
